import PropTypes from 'prop-types';
import React, { useContext } from 'react';
import { Box, Icon, QuickLink } from '@honda-canada/design-system-react';
import { useTranslation } from 'react-i18next';

import themeStyles from './DealerContactQuickLinks.style';
import useLanguage from '../../hooks/useLanguage';
import useSharedAppsBaseUrl from '../../hooks/useSharedAppsBaseUrl';
import {
  BOOK_TEST_DRIVE_GTM,
  MAKE_APPOINTMENT_GTM,
  PREFIX_TYPE,
  SCHEDULE_SERVICE_GTM,
  pushGtmDealerCardInfo,
} from '../../utils/gtmTags';
import { CONTACT_DEALER_BASE_URL } from '../../utils/constants';
import { DealersListContext } from '../DealersListContext';
import { useVariant } from '../../context/VariantContext';
import variantsConfig from './variantsConfig';

const QuickLinkContainer = themeStyles.apply(Box, 'QuickLinkContainer');

const DealerContactQuickLink = ({ dealerCode, iconColor, queryParam }) => {
  const { t } = useTranslation();
  const language = useLanguage();
  const variant = useVariant();
  const { bookTestDriveIconName, makeAnAppointmentIconName, scheduleServiceIconName } =
    variantsConfig.getVariantConfig(variant);

  const { selectedDealer } = useContext(DealersListContext);

  const dealerName = selectedDealer?.name;

  const handleScheduleServiceClick = gtmInteractionName => {
    pushGtmDealerCardInfo(PREFIX_TYPE + gtmInteractionName, dealerName, dealerCode);
  };

  const handlePushGtmDealerInfo = gtmInteractionName => {
    pushGtmDealerCardInfo(PREFIX_TYPE + gtmInteractionName, dealerName, dealerCode);
  };

  const contactDealerBaseUrl = useSharedAppsBaseUrl(CONTACT_DEALER_BASE_URL);
  const dealerCodePrefix = `${queryParam ? '&' : '?'}dealerCode=`;

  let title;
  let iconName;
  let hrefQuery = queryParam;
  let dataTestId;
  let enableClickHandler = true;
  let gtmInteractionName;

  switch (queryParam) {
    case 'MakeAnAppointment':
      title = `${t('Sub-apps.Dealers.makeAppointmentButton')}`;
      iconName = makeAnAppointmentIconName;
      hrefQuery = `${contactDealerBaseUrl}?lang=${language}&providerService=${queryParam}${dealerCodePrefix}${dealerCode}`;
      dataTestId = 'cy-quicklink-makeAnAppointment';
      enableClickHandler = false;
      gtmInteractionName = MAKE_APPOINTMENT_GTM;
      break;
    case 'BookATestDrive':
      title = `${t('Sub-apps.Dealers.bookTestDriveButton')}`;
      iconName = bookTestDriveIconName;
      hrefQuery = `${contactDealerBaseUrl}?lang=${language}&providerService=${queryParam}${dealerCodePrefix}${dealerCode}`;
      dataTestId = 'cy-quicklink-bookATestDrive';
      enableClickHandler = false;
      gtmInteractionName = BOOK_TEST_DRIVE_GTM;
      break;
    default:
      title = `${t('Sub-apps.Dealers.scheduleServiceButton')}`;
      iconName = scheduleServiceIconName;
      dataTestId = 'cy-quicklink-scheduleService';
      gtmInteractionName = SCHEDULE_SERVICE_GTM;
      break;
  }

  const handleClick = enableClickHandler
    ? () => handleScheduleServiceClick(gtmInteractionName)
    : () => handlePushGtmDealerInfo(gtmInteractionName);

  return (
    <QuickLinkContainer data-testid={dataTestId} onClick={handleClick}>
      <QuickLink
        title={title}
        variantName="primary"
        layout="inline"
        showExternalIcon
        href={hrefQuery}
        icon={<Icon iconColor={iconColor} name={iconName} />}
        target="_blank"
      />
    </QuickLinkContainer>
  );
};

DealerContactQuickLink.propTypes = {
  dealerCode: PropTypes.string,
  iconColor: PropTypes.string,
  queryParam: PropTypes.string,
};

export default DealerContactQuickLink;
